/**
 * Modal pour associer/dissocier des permissions à un rôle
 */

import React, { useState } from 'react';
import { XMarkIcon, PlusIcon, TrashIcon, MagnifyingGlassIcon } from '@heroicons/react/24/outline';
import { AssignPermissionModalProps, AssignPermissionToRoleData, RemovePermissionFromRoleData } from '@/types/permissionManagement';

const AssignPermissionModal: React.FC<AssignPermissionModalProps> = ({
  isOpen,
  onClose,
  role,
  availablePermissions,
  assignedPermissions,
  onAssign,
  onRemove
}) => {
  const [searchTerm, setSearchTerm] = useState('');
  const [selectedCategory, setSelectedCategory] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  // Fonction utilitaire pour s'assurer d'avoir un tableau
  const ensureArray = (value: any): any[] => {
    if (Array.isArray(value)) return value;
    if (value === null || value === undefined) return [];
    return [];
  };

  // Obtenir les IDs des permissions déjà assignées
  const assignedPermissionIds = new Set(ensureArray(assignedPermissions).map(p => p.id));

  // Filtrer les permissions disponibles (non assignées)
  const filteredAvailablePermissions = ensureArray(availablePermissions).filter(permission => {
    const matchesSearch = permission.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
                         permission.code.toLowerCase().includes(searchTerm.toLowerCase());
    const matchesCategory = !selectedCategory || permission.category === selectedCategory;
    const notAssigned = !assignedPermissionIds.has(permission.id);
    
    return matchesSearch && matchesCategory && notAssigned;
  });

  // Obtenir les catégories uniques
  const categories = Array.from(new Set(ensureArray(availablePermissions).map(p => p.category))).sort();

  const handleAssignPermission = async (permissionId: number) => {
    setLoading(true);
    setError(null);
    
    try {
      const data: AssignPermissionToRoleData = {
        role_id: role.id,
        permission_id: permissionId,
        access_level: 'admin' // Par défaut, on donne le niveau admin (minuscules)
      };
      await onAssign(data);
    } catch (err: any) {
      setError(err.response?.data?.message || 'Erreur lors de l\'association');
    } finally {
      setLoading(false);
    }
  };

  const handleRemovePermission = async (permissionId: number) => {
    setLoading(true);
    setError(null);
    
    try {
      const data: RemovePermissionFromRoleData = {
        role_id: role.id,
        permission_id: permissionId
      };
      await onRemove(data);
    } catch (err: any) {
      setError(err.response?.data?.message || 'Erreur lors de la suppression');
    } finally {
      setLoading(false);
    }
  };

  const getAccessLevelColor = (level: string) => {
    switch (level) {
      case 'READ': return 'bg-blue-100 text-blue-800';
      case 'WRITE': return 'bg-green-100 text-green-800';
      case 'ADMIN': return 'bg-red-100 text-red-800';
      default: return 'bg-gray-100 text-gray-800';
    }
  };

  const getAccessLevelLabel = (level: string) => {
    switch (level) {
      case 'READ': return 'Lecture';
      case 'WRITE': return 'Écriture';
      case 'ADMIN': return 'Admin';
      default: return level;
    }
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
      <div className="bg-white rounded-lg shadow-xl w-full max-w-6xl mx-4 max-h-[90vh] overflow-hidden">
        {/* Header */}
        <div className="flex items-center justify-between p-6 border-b border-gray-200">
          <div>
            <h2 className="text-xl font-semibold text-gray-900">
              Gestion des permissions - {role.display_name}
            </h2>
            <p className="text-sm text-gray-600 mt-1">
              {ensureArray(assignedPermissions).length} permission(s) assignée(s)
            </p>
          </div>
          <button
            onClick={onClose}
            className="text-gray-400 hover:text-gray-600 transition-colors"
          >
            <XMarkIcon className="h-6 w-6" />
          </button>
        </div>

        {/* Error message */}
        {error && (
          <div className="mx-6 mt-4 p-3 bg-red-100 border border-red-400 text-red-700 rounded">
            {error}
          </div>
        )}

        <div className="flex h-[calc(90vh-200px)]">
          {/* Available Permissions */}
          <div className="flex-1 p-6 border-r border-gray-200">
            <div className="mb-4">
              <h3 className="text-lg font-medium text-gray-900 mb-3">
                Permissions disponibles ({filteredAvailablePermissions.length})
              </h3>
              
              {/* Search and Filter */}
              <div className="space-y-3">
                <div className="relative">
                  <MagnifyingGlassIcon className="absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-gray-400" />
                  <input
                    type="text"
                    placeholder="Rechercher une permission..."
                    value={searchTerm}
                    onChange={(e) => setSearchTerm(e.target.value)}
                    className="w-full pl-10 pr-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                  />
                </div>
                
                <select
                  value={selectedCategory}
                  onChange={(e) => setSelectedCategory(e.target.value)}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                >
                  <option value="">Toutes les catégories</option>
                  {categories.map(category => (
                    <option key={category} value={category}>{category}</option>
                  ))}
                </select>
              </div>
            </div>

            {/* Available Permissions List */}
            <div className="space-y-2 max-h-96 overflow-y-auto">
              {filteredAvailablePermissions.map(permission => (
                <div
                  key={permission.id}
                  className="flex items-center justify-between p-3 border border-gray-200 rounded-lg hover:bg-gray-50"
                >
                  <div className="flex-1">
                    <div className="flex items-center space-x-2">
                      <span className="font-medium text-gray-900">{permission.name}</span>
                      <span className={`px-2 py-1 text-xs rounded-full ${getAccessLevelColor(permission.access_level)}`}>
                        {getAccessLevelLabel(permission.access_level)}
                      </span>
                    </div>
                    <p className="text-sm text-gray-600 mt-1">{permission.code}</p>
                    <p className="text-xs text-gray-500 mt-1">{permission.description}</p>
                  </div>
                  <button
                    onClick={() => handleAssignPermission(permission.id)}
                    disabled={loading}
                    className="ml-3 p-2 text-green-600 hover:text-green-700 hover:bg-green-50 rounded-md transition-colors disabled:opacity-50"
                    title="Assigner cette permission"
                  >
                    <PlusIcon className="h-4 w-4" />
                  </button>
                </div>
              ))}
              
              {filteredAvailablePermissions.length === 0 && (
                <div className="text-center py-8 text-gray-500">
                  Aucune permission disponible
                </div>
              )}
            </div>
          </div>

          {/* Assigned Permissions */}
          <div className="flex-1 p-6">
            <h3 className="text-lg font-medium text-gray-900 mb-4">
              Permissions assignées ({ensureArray(assignedPermissions).length})
            </h3>

            <div className="space-y-2 max-h-96 overflow-y-auto">
              {ensureArray(assignedPermissions).map(permission => (
                <div
                  key={permission.id}
                  className="flex items-center justify-between p-3 border border-gray-200 rounded-lg bg-gray-50"
                >
                  <div className="flex-1">
                    <div className="flex items-center space-x-2">
                      <span className="font-medium text-gray-900">{permission.name}</span>
                      <span className={`px-2 py-1 text-xs rounded-full ${getAccessLevelColor(permission.access_level)}`}>
                        {getAccessLevelLabel(permission.access_level)}
                      </span>
                    </div>
                    <p className="text-sm text-gray-600 mt-1">{permission.code}</p>
                    <p className="text-xs text-gray-500 mt-1">{permission.description}</p>
                  </div>
                  <button
                    onClick={() => handleRemovePermission(permission.id)}
                    disabled={loading}
                    className="ml-3 p-2 text-red-600 hover:text-red-700 hover:bg-red-50 rounded-md transition-colors disabled:opacity-50"
                    title="Retirer cette permission"
                  >
                    <TrashIcon className="h-4 w-4" />
                  </button>
                </div>
              ))}
              
              {ensureArray(assignedPermissions).length === 0 && (
                <div className="text-center py-8 text-gray-500">
                  Aucune permission assignée
                </div>
              )}
            </div>
          </div>
        </div>

        {/* Footer */}
        <div className="flex justify-end p-6 border-t border-gray-200">
          <button
            onClick={onClose}
            className="px-4 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-md hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-blue-500"
          >
            Fermer
          </button>
        </div>
      </div>
    </div>
  );
};

export default AssignPermissionModal;
